class BundleMetaReference extends HTMLElement {
  constructor() {
    super();

    this.selectedItems = new Map();

    this.sectionId = this.dataset.sectionId;
    this.discountCode = this.dataset.discountCode || '';
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
    this.cartUrl = this.dataset.cartUrl || '/cart';

    this.params = new URLSearchParams(window.location.search);
    this.bundleKey = this.normalizeKey(this.params.get('bundle') || '');
    this.mainProductHandle = this.params.get('main_product') || '';
    this.mainVariantId = this.params.get('variant') || '';

    this.mainProduct = null;
    this.mainProductPrice = 0;
    this.mainProductTitle = '';
    this.mainProductImage = '';
  }

  connectedCallback() {
    this.bundleBlocks = Array.from(this.querySelectorAll('[data-bundle-block]'));
    this.emptyState = this.querySelector('[data-empty-state]');

    this.activeBlock = this.bundleBlocks.find((block) => {
      return this.normalizeKey(block.dataset.bundleKey || '') === this.bundleKey;
    });

    if (!this.activeBlock || !this.mainProductHandle || !this.mainVariantId) {
      this.showEmptyState();
      return;
    }

    this.activateBlock();
    this.cacheElements();
    this.bindCards();
    this.bindAddButton();
    this.initHeroGallery();

    this.loadMainProduct();
  }

  normalizeKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  activateBlock() {
    this.bundleBlocks.forEach((block) => {
      block.hidden = block !== this.activeBlock;
    });

    if (this.emptyState) this.emptyState.hidden = true;
    this.hidden = false;
  }

  showEmptyState() {
    this.bundleBlocks.forEach((block) => {
      block.hidden = true;
    });

    if (this.emptyState) this.emptyState.hidden = false;
    this.hidden = false;
  }

  cacheElements() {
    this.selectedItemsEl = this.activeBlock.querySelector('[data-selected-items]');
    this.selectedCountEls = this.activeBlock.querySelectorAll('[data-selected-count]');
    this.subtotalEls = this.activeBlock.querySelectorAll('[data-subtotal]');
    this.totalEls = this.activeBlock.querySelectorAll('[data-total]');
    this.messageEl = this.activeBlock.querySelector('[data-message]');
    this.addBtn = this.activeBlock.querySelector('[data-add-bundle]');

    this.mainSummaryImage = this.activeBlock.querySelector('[data-main-summary-image]');
    this.mainSummaryTitle = this.activeBlock.querySelector('[data-main-summary-title]');
    this.mainSummaryPrice = this.activeBlock.querySelector('[data-main-summary-price]');

    this.mainProductImageEls = this.activeBlock.querySelectorAll('[data-main-product-image]');
    this.mainProductThumbEls = this.activeBlock.querySelectorAll('[data-main-product-thumb]');
  }

  async loadMainProduct() {
    try {
      const response = await fetch(`/products/${encodeURIComponent(this.mainProductHandle)}.js`, {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) throw new Error('Main product could not be loaded.');

      const product = await response.json();
      const selectedVariant = product.variants.find((variant) => {
        return String(variant.id) === String(this.mainVariantId);
      }) || product.variants.find((variant) => variant.available) || product.variants[0];

      if (!selectedVariant) throw new Error('Main product variant could not be found.');

      this.mainProduct = product;
      this.mainVariantId = String(selectedVariant.id);
      this.mainProductTitle = product.title;
      this.mainProductPrice = Number(selectedVariant.price || 0);

      const featuredImage = selectedVariant.featured_image?.src || product.featured_image || product.images?.[0] || '';
      this.mainProductImage = featuredImage;

      this.updateMainProductUI();
      this.renderAll();
    } catch (error) {
      this.setMessage(error.message || 'Could not load main product.', 'error');
    }
  }

  updateMainProductUI() {
    if (this.mainSummaryImage && this.mainProductImage) {
      this.mainSummaryImage.src = this.mainProductImage;
      this.mainSummaryImage.alt = this.mainProductTitle;
    }

    if (this.mainSummaryTitle) {
      this.mainSummaryTitle.textContent = this.mainProductTitle;
    }

    if (this.mainSummaryPrice) {
      this.mainSummaryPrice.textContent = this.formatMoney(this.mainProductPrice);
    }

    this.mainProductImageEls.forEach((img) => {
      if (!this.mainProductImage) return;
      img.src = this.mainProductImage;
      img.alt = this.mainProductTitle;
    });

    this.mainProductThumbEls.forEach((img) => {
      if (!this.mainProductImage) return;
      img.src = this.mainProductImage;
      img.alt = this.mainProductTitle;
    });
  }

  bindCards() {
    this.activeBlock.querySelectorAll('[data-bundle-card]').forEach((card) => {
      if (card.dataset.bundleCardBound === 'true') return;
      card.dataset.bundleCardBound = 'true';

      const checkbox = card.querySelector('[data-bundle-checkbox]');
      const toggle = card.querySelector('[data-bundle-toggle]');

      if (!checkbox || !toggle) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (checkbox.disabled) return;

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });

      card.addEventListener('click', (event) => {
        const interactiveTarget = event.target.closest(
          'a, button, input, select, textarea, quick-add-drawer, quick-add'
        );

        if (interactiveTarget) return;
        if (checkbox.disabled) return;

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });

      checkbox.addEventListener('change', () => {
        const item = {
          variantId: card.dataset.variantId,
          title: card.dataset.productTitle,
          handle: card.dataset.productHandle,
          price: Number(card.dataset.productPrice || 0),
          image: card.dataset.productImage || ''
        };

        if (checkbox.checked) {
          this.selectedItems.set(String(item.variantId), item);
          card.classList.add('is-selected');
          toggle.setAttribute('aria-label', `Remove ${item.title}`);
        } else {
          this.selectedItems.delete(String(item.variantId));
          card.classList.remove('is-selected');
          toggle.setAttribute('aria-label', `Select ${item.title}`);
        }

        this.renderAll();
      });
    });
  }

  bindAddButton() {
    if (!this.addBtn || this.addBtn.dataset.bundleAddBound === 'true') return;

    this.addBtn.dataset.bundleAddBound = 'true';
    this.addBtn.addEventListener('click', () => this.addBundleToCart());
  }

  initHeroGallery() {
    const slider = this.activeBlock.querySelector('[data-hero-slider]');
    if (!slider) return;

    const track = slider.querySelector('[data-hero-track]');
    const prev = slider.querySelector('[data-hero-prev]');
    const next = slider.querySelector('[data-hero-next]');
    const thumbs = Array.from(slider.querySelectorAll('[data-hero-thumb]'));

    if (!track) return;

    let index = 0;

    const setIndex = (nextIndex) => {
      const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
      const freshThumbs = Array.from(slider.querySelectorAll('[data-hero-thumb]'));

      if (!slides.length) return;

      index = Math.max(0, Math.min(nextIndex, slides.length - 1));
      track.style.transform = `translateX(-${index * 100}%)`;

      freshThumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (prev) prev.hidden = slides.length <= 1;
      if (next) next.hidden = slides.length <= 1;
    };

    thumbs.forEach((thumb, thumbIndex) => {
      thumb.addEventListener('click', () => setIndex(thumbIndex));
    });

    if (prev) {
      prev.addEventListener('click', () => {
        const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
        setIndex(index === 0 ? slides.length - 1 : index - 1);
      });
    }

    if (next) {
      next.addEventListener('click', () => {
        const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
        setIndex(index === slides.length - 1 ? 0 : index + 1);
      });
    }

    setIndex(0);
  }

  renderAll() {
    this.renderSelectedItems();
    this.renderTotals();
  }

  renderSelectedItems() {
    if (!this.selectedItemsEl) return;

    this.selectedItemsEl.querySelectorAll('[data-selected-extra]').forEach((item) => item.remove());

    this.selectedItems.forEach((item) => {
      const row = document.createElement('div');
      row.className = 'bundle-meta-reference__selected-item';
      row.dataset.selectedExtra = item.variantId;

      row.innerHTML = `
        ${
          item.image
            ? `<img src="${this.escapeHtml(item.image)}" alt="" class="bundle-meta-reference__selected-img" loading="lazy">`
            : `<div class="bundle-meta-reference__selected-img"></div>`
        }
        <div class="bundle-meta-reference__selected-info">
          <strong>${this.escapeHtml(item.title)}</strong>
          <span>Bundle item</span>
          <b>${this.formatMoney(item.price)}</b>
        </div>
        <button type="button" class="bundle-meta-reference__remove" aria-label="Remove ${this.escapeHtml(item.title)}">×</button>
      `;

      row.querySelector('button').addEventListener('click', () => {
        this.removeItem(item.variantId);
      });

      this.selectedItemsEl.appendChild(row);
    });

    this.selectedCountEls.forEach((el) => {
      el.textContent = String(this.selectedItems.size + 1);
    });
  }

  renderTotals() {
    const extrasTotal = Array.from(this.selectedItems.values()).reduce((sum, item) => sum + item.price, 0);
    const total = this.mainProductPrice + extrasTotal;

    this.subtotalEls.forEach((el) => {
      el.textContent = this.formatMoney(total);
    });

    this.totalEls.forEach((el) => {
      el.textContent = this.formatMoney(total);
    });
  }

  removeItem(variantId) {
    const key = String(variantId);
    this.selectedItems.delete(key);

    const card = this.activeBlock.querySelector(`[data-bundle-card][data-variant-id="${CSS.escape(key)}"]`);
    const checkbox = card?.querySelector('[data-bundle-checkbox]');
    const toggle = card?.querySelector('[data-bundle-toggle]');

    if (checkbox) checkbox.checked = false;
    if (card) card.classList.remove('is-selected');

    if (toggle && card) {
      toggle.setAttribute('aria-label', `Select ${card.dataset.productTitle || 'product'}`);
    }

    this.renderAll();
  }

  async addBundleToCart() {
    if (!this.mainVariantId) {
      this.setMessage('Main product variant is missing.', 'error');
      return;
    }

    this.setLoading(true);
    this.setMessage('');

    const bundleId = `bundle-${Date.now()}`;

    const items = [
      {
        id: Number(this.mainVariantId),
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'main',
          _bundle_code: this.discountCode,
          _bundle_handle: this.mainProductHandle,
          _bundle_key: this.bundleKey
        }
      },
      ...Array.from(this.selectedItems.values()).map((item) => ({
        id: Number(item.variantId),
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'item',
          _bundle_parent: this.mainProductHandle,
          _bundle_code: this.discountCode,
          _bundle_key: this.bundleKey
        }
      }))
    ];

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        let message = 'Bundle could not be added to cart.';

        try {
          const error = await response.json();
          message = error.description || error.message || message;
        } catch (error) {}

        throw new Error(message);
      }

      this.setMessage('Bundle added to cart successfully.', 'success');

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));

      window.setTimeout(() => {
        window.location.href = this.cartUrl;
      }, 350);
    } catch (error) {
      this.setMessage(error.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.addBtn) return;

    this.addBtn.disabled = isLoading;

    const text = this.addBtn.querySelector('.bundle-meta-reference__add-text');
    const icon = this.addBtn.querySelector('.bundle-meta-reference__add-icon');

    if (!text) return;

    if (isLoading) {
      text.dataset.originalText = text.textContent;
      text.textContent = 'Adding...';
      if (icon) icon.classList.add('is-spinning');
    } else {
      if (text.dataset.originalText) text.textContent = text.dataset.originalText;
      if (icon) icon.classList.remove('is-spinning');
    }
  }

  setMessage(message, type = '') {
    if (!this.messageEl) return;

    this.messageEl.textContent = message;
    this.messageEl.classList.remove('is-success', 'is-error');

    if (type) this.messageEl.classList.add(`is-${type}`);
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency
    }).format(Number(cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('bundle-meta-reference')) {
  customElements.define('bundle-meta-reference', BundleMetaReference);
}